import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { OFF } from '../../lib/off';
import { useSearchParams } from 'react-router';
import {
  AGENT_FEATURE_SET,
  CATALOG,
  FEATURE_SETS,
  checkValue,
  displayOf,
  formatMenu,
  normValue,
  parseMenu,
  showValue,
  type FieldDef,
  type FieldOption,
  type MenuOption,
  type OptionList
} from '@stitch/pbx-adapter/catalog';
import {
  describeReferences,
  duration,
  greetingFor,
  parentMenus,
  referencesTo,
  type Reference,
  type RoutingAccount,
  joinNames,
  nameOf,
  notes as notesFor,
  parseHours,
  queueGreetingFor,
  suggestNumber,
  summarize,
  warnings,
  weekSummary
} from '@stitch/pbx-adapter/describe';
import { companyLengthFrom, extensionProblem, isNumberedKind } from '@stitch/pbx-adapter/extensions';
import { api, ApiError, type Account, type AccountKind, type AccountsView, type ChangeSetView, type Directory, type Intent, type KindDef, type QueueLimit, type Wallboard } from '../../api/client';
import { CallFlow } from '../../components/CallFlow';
import { GreetingEditor } from '../../components/GreetingEditor';
import { HolidaysEditor } from '../../components/HolidaysEditor';
import { HoursEditor, durationOptions } from '../../components/HoursEditor';
import { DestinationPicker, MemberPicker } from '../../components/pickers';
import { ReviewDrawer } from '../../components/ReviewDrawer';
import { SavedCopiesCard } from '../../components/SavedCopies';
import { useTenant } from '../../components/Shell';
import { TeamTextingDrawer, useCompanyAdmin } from '../../components/TextRouting';
import { Button, Card, Chip, Dialog, Drawer, Empty, Field, LoadError, Loading, Notice, PageHeader, SelectField, TextAreaField, Toast } from '../../components/ui';
import { plural } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { quickChange } from '../../lib/quick';
import { useData } from '../../lib/useData';
import { ruleOnNow, ruleZone } from '@stitch/pbx-adapter/hours';

// Conference rooms are Stitch's own hidden presence room (1.12.18): no page.
type Kind = Exclude<AccountKind, 'person' | 'phone' | 'number' | 'conference'>;
type Values = Record<string, string>;
type Staged =
  | { op: 'add'; extension: string; values: Values }
  | { op: 'edit'; extension: string; name: string; set: Values }
  | { op: 'remove'; extension: string; name: string };

const NUMBER = /^[A-Za-z0-9]{1,32}$/;
const list = (v: string | undefined) => (v ?? '').split(/[\s,;]+/).filter(Boolean);
const dash = <span className="muted">–</span>;

/** Per-feature words, and the one live column worth a glance. Settings come from the catalog. */
/** `zone`: the company's time zone, which time of day rules follow unless they have their own. */
type LiveCell = (a: Account, v: Values, open: (a: Account) => void, zone: string) => ReactNode;
const PAGES: Record<Kind, { intro: string; empty: string; defaults?: Values; live?: { header: string; cell: LiveCell } }> = {
  ringGroup: {
    intro: 'Rings several phones at once, or in stages, until someone answers.',
    empty: 'Add a ring group to ring a team together, for example the front desk.',
    defaults: { stage1Seconds: '20' }
  },
  callQueue: {
    intro: 'Callers wait in line and go to the next free agent.',
    empty: 'Add a call queue for a team that answers calls in turn, such as support.',
    defaults: { announcePosition: 'true' },
    live: {
      header: 'Agents',
      cell: (a, v, open) => (
        <button type="button" className="link-btn agents-link" onClick={() => open(a)} aria-label={`Agents of ${displayOf('callQueue', a.extension, v)}`}>
          {a.live?.loggedIn ? `${a.live.loggedIn.length} of ${list(v.agents).length} signed in` : plural(list(v.agents).length, 'agent')}
          <Icon name="chev" size={14} />
        </button>
      )
    }
  },
  autoAttendant: {
    intro: 'Answers with a greeting and a menu: press 1 for sales, 2 for support.',
    empty: 'Add an auto attendant to answer your main number with a menu.',
    defaults: { timeoutSeconds: '10' }
  },
  timeRule: {
    intro: 'Business hours and night mode. Ring groups and auto attendants use them to send calls elsewhere after hours.',
    empty: 'Add your business hours, then use them in a ring group or auto attendant.',
    defaults: { mode: 'schedule', hoursMeaning: 'off' },
    live: {
      header: 'Right now',
      // Worked out from the rule's own hours, holidays and zone, as the phone system does.
      cell: (_a, v, _open, zone) => {
        const on = ruleOnNow(v, zone);
        return <Chip small tone={on ? 'warn' : 'ok'}>{on ? 'On' : 'Off'}</Chip>;
      }
    }
  },
  callPark: {
    intro: 'Park a call on an orbit, then pick it up from any phone.',
    empty: 'Add a park orbit so people can park calls and pick them up elsewhere.',
    defaults: { reminderSeconds: '120' }
  }
};

/** Labels of choices the phone system provides, as loaded (for describing staged changes). */
const optionLabels = new Map<string, string>();

/** A setting's value the way people read it: names, durations, hours. */
function fieldText(kind: AccountKind, f: FieldDef | undefined, value: string, dir: Directory | undefined): string {
  const v = value.trim();
  if (!f) return v;
  switch (f.type) {
    case 'destination':
    case 'rule':
      return v ? nameOf(dir, v) : '(none)';
    case 'members':
      return joinNames(list(v).map((m) => nameOf(dir, m))) || '(none)';
    case 'menu':
      return parseMenu(v).map((o) => `${o.key}: ${nameOf(dir, o.destination)}`).join(', ') || '(none)';
    case 'seconds':
      return v === '0' ? 'none' : v ? duration(v) : 'default';
    case 'recording':
      return v ? 'a new recording' : 'none (read out the text)';
    case 'pbxChoice':
      return v ? (optionLabels.get(`${f.optionsFrom}:${v}`) ?? v) : 'default';
    case 'hours': {
      const iv = parseHours(v);
      return iv === null ? v : weekSummary({ mon: v }).replace(/^Mon /, '') || 'closed';
    }
    default:
      return showValue(kind, f.name, v);
  }
}

function describe(kind: Kind, def: KindDef, s: Staged, dir: Directory | undefined): string {
  switch (s.op) {
    case 'add':
      return `Add ${def.singular} ${displayOf(kind, s.extension, s.values)} at ${s.extension}`;
    case 'edit':
      return `Change ${s.name} (${s.extension}): ${Object.entries(s.set)
        .map(([k, v]) => {
          const f = def.fields.find((x) => x.name === k);
          return `${f?.noun ?? k} → ${fieldText(kind, f, v, dir)}`;
        })
        .join('; ')}`;
    case 'remove':
      return `Remove ${s.name} (${s.extension})`;
  }
}

function toIntent(kind: Kind, s: Staged): Intent {
  switch (s.op) {
    case 'add':
      return { op: 'account.create', kind, extension: s.extension, values: s.values };
    case 'edit':
      return { op: 'account.update', kind, extension: s.extension, set: s.set };
    case 'remove':
      return { op: 'account.delete', kind, extension: s.extension };
  }
}

// ---- Page -------------------------------------------------------------------------------------

/**
 * A company at its plan's call queue limit (Growth: 2, 3157): why Add a call queue waits, and the plan
 * with unlimited queues, with a way to ask Stitch about it.
 */
function QueueLimitNotice({ limit, count, tenant }: { limit: QueueLimit; count: number; tenant: string }) {
  const [asked, setAsked] = useState<'no' | 'sending' | 'sent' | 'failed'>('no');
  const ask = async () => {
    setAsked('sending');
    try {
      await api.askSupport({
        message: `We’d like more than ${limit.max} call ${limit.max === 1 ? 'queue' : 'queues'}. Please tell us about moving our queue agents to ${limit.upgrade ?? 'a plan with more'}.`,
        page: window.location.pathname,
        tenant
      });
      setAsked('sent');
    } catch {
      setAsked('failed');
    }
  };
  return (
    <Notice tone="warn" title={`Your ${limit.plan ?? ''} plan includes up to ${plural(limit.max, 'call queue')}`}>
      You have {count}.{' '}
      {limit.upgrade
        ? `${limit.upgrade} has unlimited call queues, with wallboards, listen, whisper and barge, and skills routing. Your people can be on a mix of plans: only your queue agents need to move.`
        : 'Ask Stitch about a plan with more.'}
      <span style={{ display: 'block', marginTop: 8 }}>
        {asked === 'sent' ? (
          <span className="muted">Sent. Stitch support will reply by email.</span>
        ) : (
          <Button variant="secondary" size="small" disabled={asked === 'sending'} onClick={() => void ask()}>
            {asked === 'sending' ? 'Sending…' : `Ask about ${limit.upgrade ?? 'more queues'}`}
          </Button>
        )}
        {asked === 'failed' && <span className="crit-text"> That didn’t send. Try again, or use Help.</span>}
      </span>
    </Notice>
  );
}

export function TenantAccounts({ kind }: { kind: Kind }) {
  const { tenant } = useTenant();
  const data = useData(() => api.accounts(tenant.domain, kind), [tenant.domain, kind]);
  const directory = useData(() => api.directory(tenant.domain).catch(() => null), [tenant.domain]);
  // Every routing account, for "used by", sub-menu tags and the call flow.
  const routing = useData(() => api.routing(tenant.domain).catch(() => null), [tenant.domain]);
  const def = data.data?.def;
  const page = PAGES[kind];
  const base = `/t/${tenant.domain}`;

  const [staged, setStaged] = useState<Staged[]>([]);
  const [dialog, setDialog] = useState<DialogState>(null);
  // A sub-menu being added from a menu key, on top of the menu's own dialog.
  const [subMenu, setSubMenu] = useState<{ key: number; parent: string; reserved: string; resolve: (ext: string | null) => void } | null>(null);
  const [flowOf, setFlowOf] = useState<string | null>(null);
  const [dialogKey, setDialogKey] = useState(0);
  const [reviewing, setReviewing] = useState(false);
  const [reviewKey, setReviewKey] = useState(0);
  const [q, setQ] = useState('');
  const [agentsOf, setAgentsOf] = useState<string | null>(null);
  // Texting (1.12.32, 2960): a ring group's or queue's texting rules, for the company's own admins.
  const admin = useCompanyAdmin(tenant.domain);
  const texting = kind === 'ringGroup' || kind === 'callQueue' ? kind : null;
  const textTeams = useData(() => (admin && texting ? api.textTeams(tenant.domain).catch(() => null) : Promise.resolve(null)), [tenant.domain, admin, texting]);
  const [textingOf, setTextingOf] = useState<string | null>(null);
  // Saved copies (2718): ring groups, queues and auto attendants, for the company's admins.
  const copyable = kind === 'ringGroup' || kind === 'callQueue' || kind === 'autoAttendant' ? kind : null;
  const [copiesKey, setCopiesKey] = useState(0);
  const [savingCopy, setSavingCopy] = useState('');
  const [toast, setToast] = useState<{ message: string; tone?: 'ok' | 'crit'; undo?: ChangeSetView } | null>(null);
  const openDialog = (d: NonNullable<typeof dialog>) => {
    setDialogKey((k) => k + 1);
    setDialog(d);
  };

  const accounts = data.data?.accounts ?? [];
  const writable = (data.data?.writable ?? false) && tenant.writable;

  // Links from elsewhere (the home page): ?flow=700 opens a menu's call flow, ?flow=1 the first
  // menu's; ?edit=600 opens that item's settings.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (!data.data) return;
    const flow = params.get('flow');
    const edit = params.get('edit');
    const texts = params.get('texting');
    if (!flow && !edit && !texts) return;
    // ?texting=500 (from a person's page): that team's Texting rules.
    if (texts && texting && admin && accounts.some((a) => a.extension === texts)) setTextingOf(texts);
    if (flow && kind === 'autoAttendant') setFlowOf(accounts.some((a) => a.extension === flow) ? flow : (accounts[0]?.extension ?? null));
    const target = edit ? accounts.find((a) => a.extension === edit) : undefined;
    if (target && writable) openDialog({ mode: 'edit', account: target });
    setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.data]);
  const removable = (data.data?.removable ?? false) && tenant.writable;
  const adds = staged.filter((s): s is Extract<Staged, { op: 'add' }> => s.op === 'add');
  // A Growth company's call queues (3157): at its plan's limit, Add waits and the page says why.
  const queueLimit = kind === 'callQueue' ? (data.data?.queueLimit ?? null) : null;
  const queuesNow = accounts.length + adds.length - staged.filter((s) => s.op === 'remove').length;
  const atQueueLimit = Boolean(queueLimit && queuesNow >= queueLimit.max);

  // The directory, plus anything staged here, so new items can be picked and named right away.
  const dir: Directory | undefined = useMemo(() => {
    const base = directory.data;
    if (!base) return undefined;
    const extra = adds.filter((a) => !base.entries.some((e) => e.extension === a.extension)).map((a) => ({ extension: a.extension, kind, name: displayOf(kind, a.extension, a.values) }));
    return { ...base, entries: [...base.entries, ...extra] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directory.data, staged, kind]);

  const nameOfAccount = (a: Pick<Account, 'extension' | 'values'>) => displayOf(kind, a.extension, a.values);
  const stagedFor = (ext: string) => staged.filter((s) => s.extension === ext);
  const used = useMemo(() => new Set([...(dir?.entries.map((e) => e.extension) ?? []), ...accounts.map((a) => a.extension)]), [dir, accounts]);
  const taken = (ext: string) => used.has(ext);
  // The number after the last one used for this kind (staged additions included).
  const ofKind = [...(dir?.entries ?? []), ...accounts.map((a) => ({ extension: a.extension, kind }))];
  const lengthHere = isNumberedKind(kind) ? companyLengthFrom(dir?.entries ?? []) : null;
  const suggested = suggestNumber(kind, ofKind, { ...(dir?.mailboxPrefix ? { mailboxPrefix: dir.mailboxPrefix } : {}), alsoUsed: used, ...(lengthHere ? { length: lengthHere.length } : {}) });
  const lastOfKind = ofKind
    .filter((e) => e.kind === kind && /^\d+$/.test(e.extension))
    .map((e) => Number(e.extension))
    .reduce((m, n) => Math.max(m, n), -1);
  const suggestionHint =
    lastOfKind >= 0
      ? `The next number after ${lastOfKind}, your last ${CATALOG[kind].singular}. Change it if you like.`
      : `A good first number for ${CATALOG[kind].plural}. Change it if you like.`;

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return accounts.filter((a) => !needle || a.extension.includes(needle) || nameOfAccount(a).toLowerCase().includes(needle));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, q]);

  function stage(next: Staged) {
    setStaged((cur) => {
      const others = cur.filter((s) => !(s.extension === next.extension && s.op === next.op));
      return next.op === 'remove' ? [...others.filter((s) => s.extension !== next.extension), next] : [...others, next];
    });
  }
  const unstage = (ext: string, op?: Staged['op']) => setStaged((cur) => cur.filter((s) => !(s.extension === ext && (!op || s.op === op))));
  const shown = (a: Account): Values => {
    const edit = stagedFor(a.extension).find((s) => s.op === 'edit') as Extract<Staged, { op: 'edit' }> | undefined;
    return { ...a.values, ...(edit?.set ?? {}) };
  };
  const live = page.live;

  /** Routing accounts as they will be once the staged changes are applied. */
  const routingNow: RoutingAccount[] = useMemo(() => {
    const list = (routing.data?.accounts ?? []).filter((r) => r.kind !== kind || !staged.some((s) => s.op === 'remove' && s.extension === r.extension));
    const merged = list.map((r) => {
      if (r.kind !== kind) return r;
      const edit = staged.find((s) => s.op === 'edit' && s.extension === r.extension) as Extract<Staged, { op: 'edit' }> | undefined;
      return edit ? { ...r, values: { ...r.values, ...edit.set } } : r;
    });
    return [...merged, ...adds.filter((a) => !merged.some((r) => r.extension === a.extension)).map((a) => ({ kind, extension: a.extension, values: a.values }))];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routing.data, staged, kind]);
  const refsOf = (ext: string): Reference[] => referencesTo(ext, routingNow);
  const parentsOf = (ext: string) => (kind === 'autoAttendant' ? parentMenus(ext, routingNow) : []);

  /** Open a dialog for a sub-menu over the menu's own; resolves with its number once staged. */
  const createSubMenu = (parent: string, key: string, reserved: string) =>
    new Promise<string | null>((resolve) => setSubMenu({ key: Date.now(), parent: `${parent} – key ${key}`, reserved, resolve }));

  // Menus nothing else sends calls to: where the call flow starts.
  const topMenus = kind === 'autoAttendant' ? routingNow.filter((r) => r.kind === 'autoAttendant' && parentMenus(r.extension, routingNow).length === 0) : [];
  // Any menu can be shown (a sub-menu too, from its row); main menus get a tab each.
  const flowRoot = routingNow.find((r) => r.kind === 'autoAttendant' && r.extension === flowOf) ?? topMenus[0];
  const flowTabs = flowRoot && !topMenus.includes(flowRoot) ? [...topMenus, flowRoot] : topMenus;

  return (
    <>
      <PageHeader
        eyebrow={data.data && def ? `${plural(accounts.length, def.singular, def.plural)} · ${tenant.domain}` : tenant.domain}
        title={def?.title ?? CATALOG[kind].title}
        actions={
          <>
            <label className="search">
              <Icon name="search" size={17} strokeWidth={2} />
              <span className="sr-only">Search</span>
              <input type="search" placeholder="Search name or number" value={q} onChange={(e) => setQ(e.target.value)} />
            </label>
            <Button variant="primary" icon="plus" disabled={!writable || !def || atQueueLimit} onClick={() => openDialog({ mode: 'add' })}>
              {`Add ${CATALOG[kind].singular}`}
            </Button>
          </>
        }
      />
      <div className="content">
        {data.data?.notice ? (
          <Notice tone="warn" title="View only for now" icon="lock">
            {data.data.notice}
            {data.data.staffNote && <span className="notice-staff">Stitch staff: {data.data.staffNote}</span>}
          </Notice>
        ) : !tenant.writable ? (
          <Notice tone="warn" title="Read-only" icon="lock">
            {OFF.readOnly}
            {data.data?.staffNote && <span className="notice-staff">Stitch staff: {data.data.staffNote}</span>}
          </Notice>
        ) : (
          staged.length === 0 && <Notice title={page.intro}>Changes collect at the bottom of the screen. Review them together, then apply. Every applied change can be undone for 30 days.</Notice>
        )}
        {atQueueLimit && queueLimit && <QueueLimitNotice limit={queueLimit} count={queuesNow} tenant={tenant.domain} />}
        {kind === 'autoAttendant' && flowRoot && (
          <Card title="Call flow" action={flowTabs.length > 1 ? undefined : <span className="muted">How calls move through your menus</span>}>
            {flowTabs.length > 1 && (
              <div className="flow-tabs" role="tablist" aria-label="Menus">
                {flowTabs.map((m) => (
                  <button
                    key={m.extension}
                    type="button"
                    role="tab"
                    aria-selected={m.extension === flowRoot.extension}
                    className={`preset${m.extension === flowRoot.extension ? ' on' : ''}`}
                    onClick={() => setFlowOf(m.extension)}
                  >
                    {displayOf('autoAttendant', m.extension, m.values)}
                  </button>
                ))}
              </div>
            )}
            <div className="flow-card-wrap">
              <CallFlow root={flowRoot} accounts={routingNow} dir={dir} base={base} />
            </div>
          </Card>
        )}
        <Card>
          {data.error ? (
            <LoadError error={data.error} retry={data.reload} />
          ) : !data.data || !def ? (
            <Loading label={`Loading ${CATALOG[kind].plural}`} />
          ) : accounts.length === 0 && adds.length === 0 ? (
            <Empty title={`No ${def.plural} yet`}>
              {page.empty}
              {writable && (
                <span style={{ display: 'block', marginTop: 12 }}>
                  <Button variant="primary" icon="plus" onClick={() => openDialog({ mode: 'add' })}>
                    {`Add ${def.singular}`}
                  </Button>
                </span>
              )}
            </Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Number</th>
                    <th scope="col">Name</th>
                    <th scope="col" className="hide-sm">
                      What happens
                    </th>
                    {live && <th scope="col">{live.header}</th>}
                    {textTeams.data && <th scope="col">Texting</th>}
                    <th scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {adds.map((s) => (
                    <tr key={`add-${s.extension}`} className="staged">
                      <td className="mono">{s.extension}</td>
                      <td>
                        {displayOf(kind, s.extension, s.values)} <Chip small tone="brand">New</Chip>
                      </td>
                      <td className="hide-sm summary-cell">{summarize(kind, s.values, dir).join(' ')}</td>
                      {live && <td>{dash}</td>}
                      {textTeams.data && <td>{dash}</td>}
                      <td>
                        <div className="row-actions">
                          <button
                            type="button"
                            className="icon-btn"
                            aria-label={`Edit ${displayOf(kind, s.extension, s.values)}`}
                            title="Edit before applying"
                            onClick={() => openDialog({ mode: 'add', initial: { extension: s.extension, values: s.values }, replacing: s.extension })}
                          >
                            <Icon name="edit" size={16} />
                          </button>
                          <button type="button" className="icon-btn" aria-label={`Cancel adding ${displayOf(kind, s.extension, s.values)}`} title="Cancel" onClick={() => unstage(s.extension)}>
                            <Icon name="x" size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {rows.map((a) => {
                    const mine = stagedFor(a.extension);
                    const removing = mine.some((s) => s.op === 'remove');
                    const editing = mine.some((s) => s.op === 'edit');
                    const v = shown(a);
                    const name = nameOfAccount(a);
                    return (
                      <tr key={a.extension} className={removing ? 'staged removed' : mine.length ? 'staged' : ''}>
                        <td className="mono">{a.extension}</td>
                        <td>
                          {displayOf(kind, a.extension, v)} {removing && <Chip small tone="crit">Removing</Chip>} {editing && <Chip small tone="brand">Edited</Chip>}{' '}
                          {parentsOf(a.extension).length > 0 && (
                            <span title={`Sub-menu of ${describeReferences(parentsOf(a.extension))}`}>
                              <Chip small>Sub-menu</Chip>
                            </span>
                          )}{' '}
                          {(a.live?.calls ?? 0) > 0 && <Chip small tone="ok">{kind === 'callPark' ? `${a.live?.calls} parked` : plural(a.live?.calls ?? 0, 'call')}</Chip>}
                        </td>
                        <td className="hide-sm summary-cell">{summarize(kind, v, dir).join(' ')}</td>
                        {live && <td>{live.cell(a, v, (x) => setAgentsOf(x.extension), tenant.timeZone ?? 'America/New_York')}</td>}
                        {textTeams.data && (
                          <td>
                            <TextingButton
                              team={textTeams.data.teams.find((t) => t.extension === a.extension)}
                              name={name}
                              onOpen={() => {
                                setToast(null);
                                setTextingOf(a.extension);
                              }}
                            />
                          </td>
                        )}
                        <td>
                          <div className="row-actions">
                            {mine.length > 0 ? (
                              <Button size="small" variant="ghost" onClick={() => unstage(a.extension)}>
                                Cancel changes
                              </Button>
                            ) : (
                              <>
                                <button type="button" className="icon-btn" aria-label={`Edit ${name}`} title="Edit" disabled={!writable} onClick={() => openDialog({ mode: 'edit', account: a })}>
                                  <Icon name="edit" size={16} />
                                </button>
                                {kind === 'autoAttendant' && (
                                  <button
                    type="button"
                    className="icon-btn hide-sm"
                    aria-label={`Call flow of ${name}`}
                    title="Show the call flow"
                    onClick={() => {
                      setFlowOf(a.extension);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  >
                                    <Icon name="flow" size={16} />
                                  </button>
                                )}
                                {copyable && admin && (
                                  <button
                                    type="button"
                                    className="icon-btn hide-sm"
                                    aria-label={`Save a copy of ${name}`}
                                    title="Save a copy (to rebuild it later)"
                                    disabled={Boolean(savingCopy)}
                                    onClick={() => {
                                      setSavingCopy(a.extension);
                                      api
                                        .saveCopy(tenant.domain, copyable, a.extension)
                                        .then(() => {
                                          setToast({ message: `Saved a copy of ${name}. It’s under Saved copies.`, tone: 'ok' });
                                          setCopiesKey((k) => k + 1);
                                        })
                                        .catch((err) => setToast({ message: err instanceof ApiError ? err.message : 'The copy couldn’t be saved. Try again.', tone: 'crit' }))
                                        .finally(() => setSavingCopy(''));
                                    }}
                                  >
                                    <Icon name="archive" size={16} />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="icon-btn"
                                  aria-label={`Duplicate ${name}`}
                                  title="Duplicate"
                                  disabled={!writable}
                                  onClick={() => openDialog({ mode: 'add', initial: { values: copyOf(def, v, name) } })}
                                >
                                  <Icon name="copy" size={16} />
                                </button>
                                <button type="button" className="icon-btn" aria-label={`Remove ${name}`} title="Remove" disabled={!removable} onClick={() => openDialog({ mode: 'remove', account: a })}>
                                  <Icon name="trash" size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        {copyable && admin && (
          <SavedCopiesCard
            domain={tenant.domain}
            kind={copyable}
            writable={writable}
            refresh={copiesKey}
            onRebuild={(r) => {
              openDialog({ mode: 'add', initial: { ...(r.free && !taken(r.extension) ? { extension: r.extension } : {}), values: r.values } });
              if (r.left.length) setToast({ message: `Not brought back here (set it on the phone system if you need it): ${r.left.join(', ')}.`, tone: 'ok' });
            }}
          />
        )}
      </div>

      {staged.length > 0 && !reviewing && (
        <div className="tray" role="region" aria-label="Changes to review">
          <div className="grow">
            <strong>{plural(staged.length, 'change')} ready to review</strong>
            <span>Nothing has changed on the phone system yet.</span>
          </div>
          <Button variant="ghost" onClick={() => setStaged([])}>
            Clear
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setReviewKey((k) => k + 1);
              setReviewing(true);
            }}
          >
            Review changes
          </Button>
        </div>
      )}

      {def && data.data && (
        <AccountDialog
          key={dialogKey}
          kind={kind}
          tenant={tenant.domain}
          view={data.data}
          state={dialog}
          dir={dir}
          company={tenant.name}
          suggestedNumber={suggested}
          suggestionHint={suggestionHint}
          routing={routingNow}
          base={base}
          refs={dialog && dialog.mode === 'remove' ? refsOf(dialog.account.extension) : []}
          onNewSubMenu={kind === 'autoAttendant' ? createSubMenu : undefined}
          taken={(ext) => taken(ext) && !(dialog?.mode === 'add' && dialog.replacing === ext)}
          onClose={() => setDialog(null)}
          onAdd={(extension, values) => {
            // Editing an addition that hasn't been applied yet replaces it (its number may change too).
            if (dialog?.mode === 'add' && dialog.replacing && dialog.replacing !== extension) unstage(dialog.replacing);
            stage({ op: 'add', extension, values });
            setDialog(null);
          }}
          onEdit={(a, set) => {
            if (Object.keys(set).length === 0) unstage(a.extension, 'edit');
            else stage({ op: 'edit', extension: a.extension, name: nameOfAccount(a), set });
            setDialog(null);
          }}
          canSaveCopy={Boolean(copyable && admin)}
          onRemove={async (a, opts) => {
            // Saved first (2718): if the copy can't be saved, nothing is staged and the dialog says why.
            if (opts?.saveCopy && copyable) {
              await api.saveCopy(tenant.domain, copyable, a.extension, true);
              setCopiesKey((k) => k + 1);
            }
            stage({ op: 'remove', extension: a.extension, name: nameOfAccount(a) });
            setDialog(null);
          }}
        />
      )}

      {subMenu && def && data.data && (
        <AccountDialog
          key={`sub-${subMenu.key}`}
          kind="autoAttendant"
          tenant={tenant.domain}
          view={data.data}
          state={{ mode: 'add', initial: { values: { name: subMenu.parent } } }}
          dir={dir}
          company={tenant.name}
          suggestedNumber={suggestNumber('autoAttendant', ofKind, { ...(dir?.mailboxPrefix ? { mailboxPrefix: dir.mailboxPrefix } : {}), alsoUsed: [...used, subMenu.reserved], ...(lengthHere ? { length: lengthHere.length } : {}) })}
          suggestionHint="The next free number for a menu. Change it if you like."
          routing={routingNow}
          base={base}
          refs={[]}
          taken={(ext) => taken(ext) || ext === subMenu.reserved}
          subtitle="A sub-menu: callers reach it by pressing a key in another menu."
          onClose={() => {
            subMenu.resolve(null);
            setSubMenu(null);
          }}
          onAdd={(extension, values) => {
            stage({ op: 'add', extension, values });
            subMenu.resolve(extension);
            setSubMenu(null);
          }}
          onEdit={() => undefined}
          onRemove={() => undefined}
        />
      )}

      {kind === 'callQueue' && data.data && (
        <AgentsDrawer
          tenant={tenant.domain}
          queue={accounts.find((a) => a.extension === agentsOf) ?? null}
          dir={dir}
          canLogin={data.data.writableFields.includes('loggedInAgents')}
          canEdit={writable}
          onClose={() => setAgentsOf(null)}
          onEdit={(a) => {
            setAgentsOf(null);
            openDialog({ mode: 'edit', account: a });
          }}
          onChanged={(cs, message) => {
            setToast({ message, tone: 'ok', undo: cs });
            data.reload();
          }}
          onError={(message) => setToast({ message, tone: 'crit' })}
        />
      )}

      {texting && textingOf && (
        <TeamTextingDrawer
          domain={tenant.domain}
          team={{ extension: textingOf, name: nameOfAccount(accounts.find((a) => a.extension === textingOf) ?? { extension: textingOf, values: {} }), kind: texting }}
          onClose={() => setTextingOf(null)}
          onSaved={(saved) => {
            setTextingOf(null);
            setToast({ message: `Saved ${saved.team.name}’s texting rules.`, tone: 'ok' });
            textTeams.reload();
          }}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          tone={toast.tone}
          onDismiss={() => setToast(null)}
          {...(toast.undo
            ? {
                action: {
                  label: 'Undo',
                  run: () => {
                    const cs = toast.undo!;
                    setToast(null);
                    api
                      .undo(cs.id)
                      .then(() => {
                        setToast({ message: 'Undone.', tone: 'ok' });
                        data.reload();
                      })
                      .catch((e: unknown) => setToast({ message: e instanceof Error ? e.message : 'Couldn’t undo that.', tone: 'crit' }));
                  }
                }
              }
            : {})}
        />
      )}

      {def && (
        <ReviewDrawer
          key={reviewKey}
          open={reviewing}
          tenant={tenant.domain}
          items={staged.map((s) => ({ key: `${s.op}-${s.extension}`, description: describe(kind, def, s, dir), intent: toIntent(kind, s) }))}
          onUnstage={(key) => {
            const s = staged.find((x) => `${x.op}-${x.extension}` === key);
            if (s) unstage(s.extension, s.op);
          }}
          onClose={(applied) => {
            setReviewing(false);
            if (applied) {
              setStaged([]);
              data.reload();
              directory.reload();
              routing.reload();
            }
          }}
        />
      )}
    </>
  );
}

/** A ring group's or queue's Texting: whether it takes texts, and how many numbers send texts to it. */
function TextingButton({ team, name, onOpen }: { team: { takesTexts: boolean; numbers: string[] } | undefined; name: string; onOpen: () => void }) {
  if (!team) return dash;
  const state = team.takesTexts ? 'Takes texts' : 'Texts off';
  const count = team.takesTexts ? (team.numbers.length ? plural(team.numbers.length, 'number') : 'no numbers yet') : '';
  return (
    <button type="button" className="link-btn texting-link" onClick={onOpen} aria-label={`Texting rules for ${name}: ${state.toLowerCase()}${count ? `, ${count}` : ''}`}>
      <span className="cell-stack">
        <span className={team.takesTexts ? undefined : 'texts-go-off'}>{state}</span>
        {count && <small>{count}</small>}
      </span>
      <Icon name="chev" size={14} />
    </button>
  );
}

// ---- Add / edit / remove ----------------------------------------------------------------------

/** Fields with an editor of their own (hours, after hours, greetings) rather than one input each. */
const HOURS_FIELDS = new Set(['hoursMeaning', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
const AFTER_HOURS_FIELDS = new Set(['afterHoursRule', 'afterHoursDestination']);
/** Shown with the greeting it belongs to. */
const PAIRED_FIELDS = new Set(['greetingRecording']);

type DialogState =
  | {
      mode: 'add';
      /** Start from these values (a duplicate, a sub-menu, or an addition being edited again). */
      initial?: { extension?: string; values: Values };
      /** The staged addition this replaces. */
      replacing?: string;
    }
  | { mode: 'edit'; account: Account }
  | { mode: 'remove'; account: Account }
  | null;

/** A copy's starting values: every setting except live state and recordings, named "… (copy)". */
function copyOf(def: KindDef, values: Values, name: string): Values {
  const out: Values = {};
  for (const f of def.fields) if (!f.live && !f.separate && f.type !== 'recording' && values[f.name] !== undefined) out[f.name] = values[f.name] ?? '';
  if (def.nameField) out[def.nameField] = `${name} (copy)`.slice(0, 64);
  return out;
}

function AccountDialog({
  kind,
  tenant,
  view,
  state,
  dir,
  company,
  suggestedNumber,
  suggestionHint,
  routing,
  base,
  refs,
  onNewSubMenu,
  subtitle,
  taken,
  onClose,
  onAdd,
  onEdit,
  onRemove,
  canSaveCopy = false
}: {
  kind: Kind;
  tenant: string;
  suggestionHint: string;
  routing: RoutingAccount[];
  base: string;
  /** For removal: what uses this number. */
  refs: Reference[];
  /** Auto attendants: add a sub-menu for a key; resolves with its number (null if cancelled). */
  onNewSubMenu?: (parentName: string, key: string, reservedNumber: string) => Promise<string | null>;
  subtitle?: string;
  view: AccountsView;
  state: DialogState;
  dir: Directory | undefined;
  company: string;
  suggestedNumber: string;
  taken: (ext: string) => boolean;
  onClose: () => void;
  onAdd: (extension: string, values: Values) => void;
  onEdit: (a: Account, set: Values) => void;
  onRemove: (a: Account, opts?: { saveCopy: boolean }) => void | Promise<void>;
  /** Removing a ring group, queue or auto attendant: offer to save a copy first (2718). */
  canSaveCopy?: boolean;
}) {
  const def = view.def;
  // Time of day rules follow the company's clock (unless they have their own), wherever people are.
  const companyZone = useTenant().tenant.timeZone ?? 'America/New_York';
  const account = state && state.mode !== 'add' ? state.account : null;
  const defaults = PAGES[kind].defaults ?? {};
  const formFields = def.fields.filter((f) => !f.live && !f.separate);
  const lazy = view.lazyFields.filter((f) => formFields.some((x) => x.name === f));
  // Initialised once per opening (the parent changes `key`), never overwritten while typing.
  // Lazy settings (a recorded greeting) aren't in lists: they load below, and until then are unknown.
  const initial = state?.mode === 'add' ? state.initial : undefined;
  const [values, setValues] = useState<Values>(() => {
    const out: Values = {};
    for (const f of formFields) {
      if (account && lazy.includes(f.name) && account.values[f.name] === undefined) continue;
      out[f.name] = account ? (account.values[f.name] ?? '') : (initial?.values[f.name] ?? defaults[f.name] ?? '');
    }
    return out;
  });
  const [before, setBefore] = useState<Values>(() => ({ ...(account?.values ?? {}) }));
  const [lazyState, setLazyState] = useState<'idle' | 'loading' | 'done' | 'failed'>(account && lazy.length ? 'loading' : 'done');
  const [recordingSeconds, setRecordingSeconds] = useState<Record<string, number>>({});
  const [number, setNumber] = useState(initial?.extension ?? suggestedNumber);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [moreOpen, setMoreOpen] = useState(false);
  const [saveFirst, setSaveFirst] = useState(canSaveCopy);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState('');

  useEffect(() => {
    if (!account || !lazy.length || state?.mode !== 'edit') return;
    let alive = true;
    api
      .account(tenant, kind, account.extension)
      .then(({ account: full, recordings }) => {
        if (!alive) return;
        const got: Values = {};
        for (const f of lazy) got[f] = full.values[f] ?? '';
        setValues((cur) => ({ ...got, ...cur }));
        setBefore((cur) => ({ ...cur, ...got }));
        setRecordingSeconds(Object.fromEntries(Object.entries(recordings).map(([k, v]) => [k, v.seconds])));
        setLazyState('done');
      })
      .catch(() => alive && setLazyState('failed'));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!state) return null;

  const singular = def.singular;
  const name = account ? displayOf(kind, account.extension, account.values) : '';

  if (state.mode === 'remove') {
    const routes = refs.filter((r) => r.how === 'route');
    const members = refs.filter((r) => r.how === 'member');
    if (routes.length) {
      // Removing it would leave calls going nowhere: say where it's used, and stop there.
      return (
        <Dialog
          open
          onClose={onClose}
          title={`${name} is still in use`}
          subtitle={`${singular[0]?.toUpperCase()}${singular.slice(1)} ${state.account.extension}`}
          foot={
            <Button variant="primary" onClick={onClose}>
              OK
            </Button>
          }
        >
          <Notice tone="warn" title="Calls are still sent here" icon="alert">
            Removing it now would leave these callers with nowhere to go. Send them somewhere else first, then remove it.
          </Notice>
          <ul className="used-by">
            {routes.map((r) => (
              <li key={`${r.extension}-${r.field}-${r.where}`}>
                <Icon name={r.kind === 'autoAttendant' ? 'flow' : r.kind === 'ringGroup' ? 'bell' : r.kind === 'callQueue' ? 'headset' : r.kind === 'timeRule' ? 'clock' : 'park'} size={15} />
                <span>
                  <strong>{r.name}</strong> <span className="muted">({r.where})</span>
                </span>
              </li>
            ))}
          </ul>
        </Dialog>
      );
    }
    return (
      <Dialog
        open
        onClose={onClose}
        title={`Remove ${name}?`}
        subtitle={`${singular[0]?.toUpperCase()}${singular.slice(1)} ${state.account.extension}`}
        foot={
          <>
            <Button variant="ghost" onClick={onClose}>
              Keep
            </Button>
            <Button
              variant="danger"
              icon="trash"
              disabled={removing}
              onClick={() => {
                setRemoving(true);
                setRemoveError('');
                Promise.resolve(onRemove(state.account, { saveCopy: canSaveCopy && saveFirst }))
                  .catch((err) => setRemoveError(err instanceof ApiError ? err.message : 'The copy couldn’t be saved, so nothing was added. Try again, or untick saving a copy.'))
                  .finally(() => setRemoving(false));
              }}
            >
              {removing && canSaveCopy && saveFirst ? 'Saving a copy…' : 'Add to changes'}
            </Button>
          </>
        }
      >
        <Notice tone="warn" title="Only partly reversible">
          {def.removeCaveat}
        </Notice>
        {canSaveCopy && (
          <label className="check">
            <input type="checkbox" checked={saveFirst} onChange={(e) => setSaveFirst(e.target.checked)} /> Save a copy first, so it can be rebuilt later (under Saved copies)
          </label>
        )}
        {removeError && (
          <Notice tone="crit" title="Not removed">
            {removeError}
          </Notice>
        )}
        {members.length > 0 && <p className="muted">It will also be taken out of {describeReferences(members)}.</p>}
        <span className="muted">This is added to your changes. Nothing happens until you review and apply.</span>
      </Dialog>
    );
  }

  const canChange = (f: FieldDef) => view.writableFields.includes(f.name);
  const visible = (f: FieldDef) => !f.showWhen || f.showWhen.values.includes(values[f.showWhen.field] ?? '');
  const set = (field: string, value: string) => setValues((cur) => ({ ...cur, [field]: value }));
  const trimmed = Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v.trim()]));
  const summary = summarize(kind, trimmed, dir);
  const warns = warnings(kind, trimmed, dir);
  const infos = notesFor(kind, trimmed, dir);

  const submit = () => {
    const errs: Record<string, string> = {};
    if (state.mode === 'add') {
      const n = number.trim();
      // Ring groups, queues and menus: 3 to 6 digits, as many as the company's others (2986).
      const problem = isNumberedKind(kind) ? extensionProblem(n, companyLengthFrom(dir?.entries ?? [])) : NUMBER.test(n) ? null : 'Use 1 to 32 letters or digits.';
      if (problem) errs.__number = problem;
      else if (taken(n)) errs.__number = 'This number is already in use.';
    }
    const changed = (f: FieldDef) => values[f.name] !== undefined && normValue(f.type, values[f.name]) !== normValue(f.type, before[f.name]);
    for (const f of formFields) {
      const v = (values[f.name] ?? '').trim();
      if (f.required && !v) errs[f.name] = 'Required.';
      // Unchanged values are checked by the phone system already (a holiday list set elsewhere, say).
      const problem = v && (state.mode === 'add' || changed(f)) ? checkValue(f, v) : null;
      if (problem) errs[f.name] = `${problem}.`;
    }
    setErrors(errs);
    if (Object.keys(errs).length) {
      // Open "More options" if that's where the problem is.
      if (formFields.some((f) => f.more && errs[f.name])) setMoreOpen(true);
      return;
    }
    if (state.mode === 'add') {
      const out: Values = {};
      for (const f of formFields) {
        if (!canChange(f)) continue;
        const v = (values[f.name] ?? '').trim();
        // A box left untouched shows Off, so it's sent as off: the phone system's own default for a
        // new one may be on (mobiles ringing for a new queue, 3135).
        if (v) out[f.name] = v;
        else if (f.type === 'toggle' && (!f.showWhen || visible(f))) out[f.name] = 'false';
      }
      return onAdd(number.trim(), out);
    }
    const diff: Values = {};
    for (const f of formFields) if (changed(f)) diff[f.name] = (values[f.name] ?? '').trim();
    onEdit(state.account, diff);
  };

  const field = (n: string) => formFields.find((f) => f.name === n);
  const writer =
    kind === 'autoAttendant'
      ? () => greetingFor(values.menu ?? '', dir, company)
      : kind === 'callQueue'
        ? () => queueGreetingFor(values.name ?? '')
        : undefined;
  const recordingField = field('greetingRecording');

  const input = (f: FieldDef) => {
    if (f.type === 'speech' && kind === 'autoAttendant' && recordingField) {
      return (
        <GreetingEditor
          key={f.name}
          tenant={tenant}
          label={f.label}
          help={f.help}
          error={errors[f.name] ?? errors.greetingRecording}
          text={values[f.name] ?? ''}
          onText={(v) => set(f.name, v)}
          recording={values.greetingRecording}
          recordingSeconds={recordingSeconds.greetingRecording}
          onRecording={(ref, seconds) => {
            set('greetingRecording', ref);
            if (seconds !== undefined) setRecordingSeconds((cur) => ({ ...cur, greetingRecording: seconds }));
          }}
          canRecord={canChange(recordingField) && lazyState !== 'failed'}
          loading={lazyState === 'loading'}
          writer={writer}
        />
      );
    }
    if (f.type === 'holidays') {
      return <HolidaysEditor key={f.name} value={values[f.name] ?? ''} onChange={(v) => set(f.name, v)} ruleOnDuringHours={values.hoursMeaning === 'on'} error={errors[f.name]} />;
    }
    return (
      <FieldInput
        key={f.name}
        kind={kind}
        tenant={tenant}
        field={f}
        value={values[f.name] ?? ''}
        error={errors[f.name]}
        dir={dir}
        locked={state.mode === 'edit' && !canChange(f)}
        writer={f.type === 'speech' ? writer : undefined}
        onChange={(v) => set(f.name, v)}
        onNewSubMenu={f.type === 'menu' && onNewSubMenu ? (key) => onNewSubMenu(values.name?.trim() || 'Menu', key, state.mode === 'add' ? number.trim() : '') : undefined}
      />
    );
  };

  /** Fields grouped by section (fields without a section first), in catalog order. */
  const group = (fields: FieldDef[]) => {
    const sections: { title: string; fields: FieldDef[] }[] = [];
    for (const f of fields) {
      const title = f.section ?? '';
      let s = sections.find((x) => x.title === title);
      if (!s) sections.push((s = { title, fields: [] }));
      s.fields.push(f);
    }
    return sections;
  };
  const main = group(formFields.filter((f) => !f.more));
  const more = group(formFields.filter((f) => f.more));
  const presets = (def.presets ?? []).filter((p) => Object.keys(p.values).some((k) => field(k) && canChange(field(k)!)));
  const moreCount = formFields.filter((f) => f.more && (values[f.name] ?? '') !== '' && normValue(f.type, values[f.name]) !== normValue(f.type, '')).length;

  const renderSection = (s: { title: string; fields: FieldDef[] }, inMore: boolean) => {
    const fields = s.fields.filter((f) => visible(f) && !HOURS_FIELDS.has(f.name) && !AFTER_HOURS_FIELDS.has(f.name) && !PAIRED_FIELDS.has(f.name));
    const hasHours = s.fields.some((f) => HOURS_FIELDS.has(f.name));
    const hasAfterHours = s.fields.some((f) => AFTER_HOURS_FIELDS.has(f.name));
    if (!fields.length && !hasHours && !hasAfterHours) return null;
    return (
      <fieldset key={`${inMore ? 'more-' : ''}${s.title || 'main'}`} className="form-section">
        {s.title && <legend>{s.title}</legend>}
        {!s.title && !inMore && (
          <div className="pair name-row">
            {fields.filter((f) => f.name === 'name').map(input)}
            {state.mode === 'add' && (
              <Field label="Number" value={number} onChange={(e) => setNumber(e.target.value)} error={errors.__number} inputMode="numeric" autoComplete="off" hint={number === suggestedNumber ? suggestionHint : undefined} />
            )}
          </div>
        )}
        <div className="form-stack">
          {pairUp(fields.filter((f) => s.title || inMore || f.name !== 'name')).map((g) =>
            g.length === 2 ? (
              // A ring stage: who rings, and for how long, side by side.
              <div className="pair" key={g[0]!.name}>
                {g.map(input)}
              </div>
            ) : (
              input(g[0]!)
            )
          )}
          {hasHours && <HoursEditor values={values} set={set} meaning={values.hoursMeaning ?? ''} onMeaning={(v) => set('hoursMeaning', v)} zone={ruleZone(values, companyZone)} />}
          {hasAfterHours && field('afterHoursRule') && <AfterHoursEditor values={values} set={set} dir={dir} />}
        </div>
      </fieldset>
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={state.mode === 'add' ? (initial?.extension ? `Edit new ${singular}` : subtitle ? 'Add a sub-menu' : `Add ${singular}`) : `Edit ${name}`}
      subtitle={subtitle ?? (state.mode === 'add' ? PAGES[kind].intro : `${singular[0]?.toUpperCase()}${singular.slice(1)} ${state.account.extension}`)}
      foot={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit}>
            Add to changes
          </Button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="account-form"
        noValidate
      >
        {main.map((s) => renderSection(s, false))}

        {more.length > 0 && (
          <div className={`more-options${moreOpen ? ' open' : ''}`}>
            <button type="button" className="more-toggle" aria-expanded={moreOpen} onClick={() => setMoreOpen((o) => !o)}>
              <Icon name="sliders" size={16} />
              <span className="grow">
                <strong>More options</strong>
                <span className="muted">{moreOpen ? 'Fine-tune how it behaves.' : moreCount ? `${plural(moreCount, 'setting')} in use. Fine-tune how it behaves.` : 'Fine-tune how it behaves.'}</span>
              </span>
              <Icon name="chev" size={16} />
            </button>
            {moreOpen && (
              <div className="more-body">
                {presets.length > 0 && (
                  <div className="presets">
                    <span className="field-label">Start from a setup</span>
                    <div className="preset-cards">
                      {presets.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className="preset-card"
                          onClick={() => {
                            for (const [k, v] of Object.entries(p.values)) {
                              const f = field(k);
                              if (f && canChange(f)) set(k, v);
                            }
                          }}
                        >
                          <strong>{p.label}</strong>
                          <span className="muted">{p.description}</span>
                        </button>
                      ))}
                    </div>
                    <span className="hint">A setup fills in the options below. Change anything you like before adding it to your changes.</span>
                  </div>
                )}
                {more.map((s) => renderSection(s, true))}
              </div>
            )}
          </div>
        )}

        <div className="preview" aria-live="polite">
          <span className="preview-title">
            <Icon name={kind === 'autoAttendant' ? 'flow' : 'phone'} size={15} /> {kind === 'autoAttendant' ? 'How calls flow' : 'What callers get'}
          </span>
          {kind === 'autoAttendant' ? (
            <CallFlow
              root={{ kind, extension: state.mode === 'add' ? number.trim() : state.account.extension, values: trimmed }}
              accounts={routing.filter((r) => r.extension !== (state.mode === 'add' ? number.trim() : state.account.extension))}
              dir={dir}
            />
          ) : (
            summary.map((line, i) => <p key={i}>{line}</p>)
          )}
          {(warns.length > 0 || infos.length > 0) && (
            <ul className="warn-list">
              {infos.map((w) => (
                <li key={w} className="info">
                  <Icon name="info" size={15} /> {w}
                </li>
              ))}
              {warns.map((w) => (
                <li key={w}>
                  <Icon name="alert" size={15} /> {w}
                </li>
              ))}
            </ul>
          )}
        </div>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

/** Members followed by a ring time render as one row. */
function pairUp(fields: FieldDef[]): FieldDef[][] {
  const out: FieldDef[][] = [];
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i]!;
    const next = fields[i + 1];
    if (f.type === 'members' && next?.type === 'seconds') {
      out.push([f, next]);
      i++;
    } else out.push([f]);
  }
  return out;
}

function AfterHoursEditor({ values, set, dir }: { values: Values; set: (field: string, value: string) => void; dir: Directory | undefined }) {
  const rules = (dir?.entries ?? []).filter((e) => e.kind === 'timeRule');
  const on = Boolean(values.afterHoursRule || values.afterHoursDestination);
  return (
    <div className="form-stack">
      <label className="check">
        <input
          type="checkbox"
          checked={on}
          onChange={(e) => {
            if (e.target.checked) set('afterHoursRule', rules[0]?.extension ?? '');
            else {
              set('afterHoursRule', '');
              set('afterHoursDestination', '');
            }
          }}
        />
        Send calls somewhere else after hours
      </label>
      {on &&
        (rules.length === 0 && !values.afterHoursRule ? (
          <Notice title="Add your business hours first">Create a time of day rule under Time of Day Rules (for example Mon–Fri 9–5), then come back and choose it here.</Notice>
        ) : (
          <div className="pair even">
            <SelectField
              label="Time of day rule"
              hint="Calls go elsewhere while this rule is on."
              value={values.afterHoursRule ?? ''}
              onChange={(e) => set('afterHoursRule', e.target.value)}
              options={[
                ...(values.afterHoursRule ? [] : [{ value: '', label: 'Choose…' }]),
                ...rules.map((r) => ({ value: r.extension, label: r.name || `Rule ${r.extension}` })),
                ...(values.afterHoursRule && !rules.some((r) => r.extension === values.afterHoursRule) ? [{ value: values.afterHoursRule, label: values.afterHoursRule }] : [])
              ]}
            />
            <DestinationPicker label="While the rule is on, send calls to" value={values.afterHoursDestination ?? ''} dir={dir} onChange={(v) => set('afterHoursDestination', v)} />
          </div>
        ))}
    </div>
  );
}

const COUNT_OPTIONS = ['5', '10', '15', '20', '30', '50'];
const SMALL_COUNTS = ['1', '2', '3', '4', '5'];

/** Choices the phone system provides, loaded once per tenant and list. */
const optionCache = new Map<string, Promise<FieldOption[]>>();
function usePbxOptions(tenant: string, list: OptionList | undefined): FieldOption[] | null {
  const [options, setOptions] = useState<FieldOption[] | null>(null);
  useEffect(() => {
    if (!list) return;
    const key = `${tenant}|${list}`;
    let p = optionCache.get(key);
    if (!p) {
      p = api.options(tenant, list).then((r) => r.options);
      optionCache.set(key, p);
      p.catch(() => optionCache.delete(key));
    }
    let alive = true;
    p.then((o) => {
      for (const x of o) optionLabels.set(`${list}:${x.value}`, x.label);
      if (alive) setOptions(o);
    }).catch(() => alive && setOptions([]));
    return () => {
      alive = false;
    };
  }, [tenant, list]);
  return options;
}

function PbxChoice({ tenant, field, value, error, onChange }: { tenant: string; field: FieldDef; value: string; error?: string; onChange: (v: string) => void }) {
  const loaded = usePbxOptions(tenant, field.optionsFrom);
  const options = loaded ?? [{ value: '', label: 'Default' }];
  const shown = value && !options.some((o) => o.value === value) ? [...options, { value, label: loaded ? `Set in the phone system (${value})` : 'Loading…' }] : options;
  return <SelectField label={field.label} hint={field.help} error={error} value={value} onChange={(e) => onChange(e.target.value)} options={shown} />;
}

function FieldInput({
  kind,
  tenant,
  field,
  value,
  error,
  dir,
  locked,
  writer,
  onChange,
  onNewSubMenu
}: {
  kind: Kind;
  tenant: string;
  field: FieldDef;
  value: string;
  error?: string;
  dir: Directory | undefined;
  /** Can be seen but not changed here (still being confirmed). */
  locked?: boolean;
  writer?: () => string;
  onChange: (v: string) => void;
  /** Menus: make a new sub-menu for a key; resolves with its number. */
  onNewSubMenu?: (key: string) => Promise<string | null>;
}) {
  const common = { label: field.label, error, hint: field.help };
  if (locked) {
    return (
      <div className="field locked">
        <span className="field-label">{field.label}</span>
        <span className="locked-value">
          {field.type === 'toggle' ? (value === 'true' ? 'On' : 'Off') : fieldText(kind, field, value, dir) || '(none)'}
          <span className="muted">
            <Icon name="lock" size={13} /> Set by Stitch for now
          </span>
        </span>
      </div>
    );
  }
  switch (field.type) {
    case 'toggle':
      return (
        <div className="field toggle-field">
          <label className="check">
            <input type="checkbox" checked={value === 'true'} onChange={(e) => onChange(e.target.checked ? 'true' : 'false')} />
            {field.label}
          </label>
          {field.help && <span className="hint">{field.help}</span>}
        </div>
      );
    case 'choice': {
      const options = field.options ?? [];
      const hasEmpty = options.some((o) => o.value === '');
      const unknown = value !== '' && !options.some((o) => o.value === value);
      if (options.some((o) => o.description)) {
        // Choices that need explaining: cards with a line each.
        return (
          <div className="field">
            <span className="field-label">{field.label}</span>
            {field.help && <span className="hint">{field.help}</span>}
            <div className="radio-cards compact" role="radiogroup" aria-label={field.label}>
              {/* An optional setting can go back to the phone system's default once something else is picked (3138). */}
              {!hasEmpty && field.more && (
                <label className="radio-card">
                  <input type="radio" name={`${field.name}-choice`} checked={value === ''} onChange={() => onChange('')} />
                  <span>
                    <strong>Phone system default</strong>
                  </span>
                </label>
              )}
              {!hasEmpty && !field.more && value === '' && (
                <label className="radio-card">
                  <input type="radio" checked readOnly />
                  <span>
                    <strong>Phone system default</strong>
                  </span>
                </label>
              )}
              {unknown && (
                <label className="radio-card">
                  <input type="radio" checked readOnly />
                  <span>
                    <strong>Set in the phone system</strong>
                    <span className="muted"> {value}</span>
                  </span>
                </label>
              )}
              {options.map((o) => (
                <label className="radio-card" key={o.value}>
                  <input type="radio" name={`${field.name}-choice`} checked={value === o.value} onChange={() => onChange(o.value)} />
                  <span>
                    <strong>{o.label}</strong>
                    {o.description && <span className="muted"> {o.description}</span>}
                  </span>
                </label>
              ))}
            </div>
            {error && <span className="error">{error}</span>}
          </div>
        );
      }
      return (
        <SelectField
          {...common}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          options={[
            // An optional setting keeps "Phone system default" to go back to (3138).
            ...(!hasEmpty && (field.more || value === '') ? [{ value: '', label: field.more ? 'Phone system default' : 'Choose…' }] : []),
            ...(unknown ? [{ value, label: `Set in the phone system (${value})` }] : []),
            ...options
          ]}
        />
      );
    }
    case 'pbxChoice':
      return <PbxChoice tenant={tenant} field={field} value={value} error={error} onChange={onChange} />;
    case 'digits':
      return <Field {...common} value={value} inputMode="numeric" autoComplete="off" maxLength={10} placeholder="No PIN" onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))} />;
    case 'destination':
      return <DestinationPicker label={field.label} help={field.help} error={error} value={value} dir={dir} onChange={onChange} />;
    case 'members':
      return (
        <MemberPicker
          label={field.label}
          error={error}
          value={value}
          dir={dir}
          mode={kind === 'callQueue' ? 'people' : 'members'}
          addLabel={kind === 'callQueue' ? 'Add agent' : 'Add person'}
          onChange={onChange}
        />
      );
    case 'seconds': {
      const empty =
        field.name === 'reminderSeconds' ? 'No reminder' : field.name === 'maxWaitSeconds' ? 'No limit' : field.name === 'wrapUpSeconds' ? 'Phone system default' : 'Default';
      return <SelectField {...common} value={value} onChange={(e) => onChange(e.target.value)} options={durationOptions(field.name, value, empty)} />;
    }
    case 'count': {
      const base = field.name === 'minAgents' ? SMALL_COUNTS : COUNT_OPTIONS;
      const opts = value && !base.includes(value) ? [...base, value].sort((a, b) => Number(a) - Number(b)) : base;
      return (
        <SelectField
          {...common}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          options={[{ value: '', label: field.name === 'minAgents' ? 'No minimum' : 'No limit' }, ...opts.map((o) => ({ value: o, label: o }))]}
        />
      );
    }
    case 'speech':
      if (value.startsWith('file:')) {
        return (
          <div className="field">
            <span className="field-label">{field.label}</span>
            <span className="recording">
              <Icon name="bell" size={15} /> Uses a recording ({value.slice(5).split('/').pop()})
              <button type="button" className="link-btn" onClick={() => onChange('')}>
                Replace with spoken text
              </button>
            </span>
          </div>
        );
      }
      return (
        <div className="speech-field">
          <TextAreaField {...common} value={value} rows={3} onChange={(e) => onChange(e.target.value)} />
          {writer && (
            <button type="button" className="link-btn write-it" onClick={() => onChange(writer())}>
              <Icon name="spark" size={14} /> Write it for me
            </button>
          )}
        </div>
      );
    case 'menu':
      return <MenuEditor field={field} value={value} error={error} dir={dir} onChange={onChange} onNewSubMenu={onNewSubMenu} />;
    default:
      return <Field {...common} value={value} autoComplete="off" type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'} onChange={(e) => onChange(e.target.value)} />;
  }
}

function MenuEditor({
  field,
  value,
  error,
  dir,
  onChange,
  onNewSubMenu
}: {
  field: FieldDef;
  value: string;
  error?: string;
  dir: Directory | undefined;
  onChange: (v: string) => void;
  onNewSubMenu?: (key: string) => Promise<string | null>;
}) {
  // Rows are kept locally so a half-made row (a key with no destination yet) isn't lost.
  const [rows, setRows] = useState<MenuOption[]>(() => parseMenu(value));
  const update = (next: MenuOption[]) => {
    setRows(next);
    onChange(formatMenu(next));
  };
  return (
    <div className="field">
      <span className="field-label">{field.label}</span>
      {field.help && <span className="hint">{field.help}</span>}
      <div className="menu-rows">
        {rows.map((r, i) => (
          <div className="menu-row" key={i}>
            <label className="menu-key">
              <span aria-hidden="true">Press</span>
              <input
                className="input mono"
                aria-label={`Key for option ${i + 1}`}
                value={r.key}
                maxLength={4}
                inputMode="numeric"
                onChange={(e) => update(rows.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))}
              />
            </label>
            <div className="menu-dest">
              <DestinationPicker
                hideLabel
                clearable={false}
                label={`Key ${r.key || i + 1} goes to`}
                value={r.destination}
                dir={dir}
                onChange={(v) => update(rows.map((x, j) => (j === i ? { ...x, destination: v } : x)))}
              />
            </div>
            {onNewSubMenu && (
              <button
                type="button"
                className="icon-btn"
                aria-label={`New sub-menu for key ${r.key || i + 1}`}
                title="Make a new sub-menu for this key"
                onClick={() => {
                  // This dialog waits underneath while the sub-menu is added, so `rows` is current.
                  void onNewSubMenu(r.key || String(i + 1)).then((ext) => {
                    if (ext) update(rows.map((x, j) => (j === i ? { ...x, destination: ext } : x)));
                  });
                }}
              >
                <Icon name="flow" size={15} />
              </button>
            )}
            <button type="button" className="icon-btn" aria-label={`Remove option ${r.key || i + 1}`} onClick={() => update(rows.filter((_, j) => j !== i))}>
              <Icon name="x" size={15} />
            </button>
          </div>
        ))}
      </div>
      {rows.length < 10 && (
        <button
          type="button"
          className="add-member"
          onClick={() => {
            const used = new Set(rows.map((r) => r.key));
            const next = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].find((k) => !used.has(k)) ?? '';
            update([...rows, { key: next, destination: '' }]);
          }}
        >
          <Icon name="plus" size={15} strokeWidth={2.2} /> Add option
        </button>
      )}
      {error && <span className="error">{error}</span>}
    </div>
  );
}

// ---- Queue agents -------------------------------------------------------------------------------

const STATE_LABEL: Record<'available' | 'busy' | 'dnd' | 'loggedOut', { label: string; tone: 'ok' | 'brand' | 'neutral' | 'warn' }> = {
  available: { label: 'Ready', tone: 'ok' },
  busy: { label: 'On a call', tone: 'brand' },
  dnd: { label: 'Do not disturb', tone: 'warn' },
  loggedOut: { label: 'Signed out', tone: 'neutral' }
};

/**
 * Who answers a queue and who is taking calls right now. Logging someone in or out happens at
 * once (with undo); adding or removing agents goes through the usual edit and review.
 */
function AgentsDrawer({
  tenant,
  queue,
  dir,
  canLogin,
  canEdit,
  onClose,
  onEdit,
  onChanged,
  onError
}: {
  tenant: string;
  queue: Account | null;
  dir: Directory | undefined;
  canLogin: boolean;
  canEdit: boolean;
  onClose: () => void;
  onEdit: (a: Account) => void;
  onChanged: (cs: ChangeSetView, message: string) => void;
  onError: (message: string) => void;
}) {
  const [board, setBoard] = useState<Wallboard | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const ext = queue?.extension;
  useEffect(() => {
    setBoard(null);
    if (!ext) return;
    let alive = true;
    // Who is on a call comes from the wallboard, where it's available; log-ins come with the list.
    api
      .wallboard(tenant)
      .then((b) => alive && setBoard(b))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [tenant, ext, queue?.values.loggedInAgents]);

  if (!queue) return null;
  const name = displayOf('callQueue', queue.extension, queue.values);
  const agents = list(queue.values.agents);
  const loggedIn = new Set(list(queue.values.loggedInAgents ?? queue.live?.loggedIn?.join(' ')));
  const live = board?.queues.find((q) => q.extension === queue.extension);
  const stateOf = (a: string) => live?.agents.find((x) => x.extension === a)?.state ?? (loggedIn.has(a) ? 'available' : 'loggedOut');
  const known = queue.values.loggedInAgents !== undefined || queue.live?.loggedIn !== undefined;

  /** Log the named agents in or out, one intent each: nobody else is touched. */
  async function setLogin(changes: { agent: string; loggedIn: boolean }[], message: string, key: string) {
    setBusy(key);
    try {
      const cs = await quickChange(
        tenant,
        changes.map((c) => ({ op: 'queue.login', extension: queue!.extension, agent: c.agent, loggedIn: c.loggedIn }))
      );
      if (cs.status === 'applied') onChanged(cs, message);
      else onError(cs.error ?? 'That didn’t go through. Nothing changed.');
    } catch (e) {
      onError(e instanceof ApiError || e instanceof Error ? e.message : 'That didn’t go through.');
    } finally {
      setBusy(null);
    }
  }

  const loggedOut = agents.filter((a) => !loggedIn.has(a));
  return (
    <Drawer
      open
      onClose={onClose}
      title={`${name} agents`}
      subtitle={`Call queue ${queue.extension}${live ? ` · ${plural(live.waiting, 'caller')} waiting` : ''}`}
      foot={
        <>
          {canEdit && (
            <Button variant="secondary" icon="edit" onClick={() => onEdit(queue)}>
              Add or remove agents
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      {!canLogin && (
        <Notice title="Signing agents in and out from here isn’t switched on yet" icon="lock">
          Agents can log in and out from their phones (*64 and *65) in the meantime.
        </Notice>
      )}
      {agents.length === 0 ? (
        <Empty title="No agents yet">Add the people who answer this queue.</Empty>
      ) : (
        <>
          {canLogin && known && (
            <div className="preset-row">
              <button
                type="button"
                className="preset"
                disabled={busy !== null || loggedOut.length === 0}
                onClick={() => void setLogin(loggedOut.map((agent) => ({ agent, loggedIn: true })), `Everyone is signed in to ${name}.`, 'all')}
              >
                Sign everyone in
              </button>
            </div>
          )}
          <ul className="agent-list">
            {agents.map((a) => {
              const info = dir?.entries.find((e) => e.extension === a);
              const st = STATE_LABEL[stateOf(a)];
              const isIn = loggedIn.has(a);
              const stats = live?.agents.find((x) => x.extension === a);
              const notAgent = info?.kind === 'person' && info.featureSet !== undefined && info.featureSet !== AGENT_FEATURE_SET;
              const who = nameOf(dir, a);
              return (
                <li key={a} className="agent-row">
                  <span className="agent-who">
                    <strong>{who}</strong>
                    <span className="muted">
                      Ext {a}
                      {stats?.answeredToday !== undefined && ` · ${stats.answeredToday} answered today`}
                    </span>
                    {notAgent && (
                      <span className="agent-warn">
                        <Icon name="alert" size={13} /> Set to {FEATURE_SETS.find((o) => o.value === info?.featureSet)?.label ?? 'another feature set'}, so may not get queue calls. Editing the agents switches them to Call queue agent.
                      </span>
                    )}
                  </span>
                  <Chip small tone={st.tone}>
                    {known || live ? st.label : 'Unknown'}
                  </Chip>
                  {canLogin && known && (
                    <Button
                      size="small"
                      variant={isIn ? 'ghost' : 'secondary'}
                      icon={isIn ? 'logout' : 'login'}
                      disabled={busy !== null}
                      aria-label={`${isIn ? 'Sign out' : 'Sign in'} ${who}`}
                      onClick={() => void setLogin([{ agent: a, loggedIn: !isIn }], `${who} is signed ${isIn ? 'out of' : 'in to'} ${name}.`, a)}
                    >
                      {busy === a ? 'Working…' : isIn ? 'Sign out' : 'Sign in'}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="muted small-print">Signing someone in or out happens right away and shows in Change history, where it can be undone.</p>
        </>
      )}
    </Drawer>
  );
}
